import { AutoComplete as AntAutoComplete } from 'antd';
import React, { useEffect, useMemo, useRef, useState } from 'react';

export interface IAutoCompleteOption {
  label: string;
  value: string | number;
}

export interface IAutoCompleteProps {
  value?: string | number;
  onChange?: (value: string | number | undefined) => void;

  options?: IAutoCompleteOption[];
  placeholder?: string;
  disabled?: boolean;
  allowClear?: boolean;
  onSelectOption?: (option: IAutoCompleteOption) => void;
}

const resolveDisplay = (
  value: string | number | undefined | null,
  options: IAutoCompleteOption[],
): string => {
  if (value === undefined || value === null || value === '') return '';
  const opt = options.find((o) => o.value === value);
  return opt?.label ?? String(value);
};

const AutoComplete: React.FC<IAutoCompleteProps> = ({
  value,
  onChange,
  options = [],
  placeholder,
  disabled,
  allowClear = true,
  onSelectOption,
}) => {
  const [displayValue, setDisplayValue] = useState<string>(() =>
    resolveDisplay(value, options),
  );

  const lastCommittedRef = useRef<string | number | undefined | null>(value);

  const labelToOption = useMemo(() => {
    const map = new Map<string, IAutoCompleteOption>();
    options.forEach((o) => map.set(o.label, o));
    return map;
  }, [options]);

  useEffect(() => {
    if (value === lastCommittedRef.current) return;
    lastCommittedRef.current = value;
    setDisplayValue(resolveDisplay(value, options));
  }, [value]);

  const commit = (v: string | number | undefined) => {
    lastCommittedRef.current = v;
    onChange?.(v);
  };

  const handleSelect = (selectedValue: string | number, option: IAutoCompleteOption) => {
    setDisplayValue(option.label);
    commit(selectedValue);
    onSelectOption?.(option);
  };

  const handleSearch = (text: string) => {
    setDisplayValue(text);

    if (text === '') {
      commit(undefined);
      return;
    }

    const matched = labelToOption.get(text);
    commit(matched ? matched.value : text);
  };

  const handleClear = () => {
    setDisplayValue('');
    commit(undefined);
  };

  return (
    <AntAutoComplete
      value={displayValue}
      options={options}
      onSelect={handleSelect as any}
      onSearch={handleSearch}
      onClear={handleClear}
      placeholder={placeholder}
      disabled={disabled}
      allowClear={allowClear}
      filterOption={(input, option) =>
        (option?.label as string)?.toLowerCase().includes(input.toLowerCase())
      }
    />
  );
};

export default AutoComplete;
